import React, { useCallback, useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { FileDown, Sheet } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { PageHead, Field, Stat, Empty, PortPicker, usePorts, CONTAINERS, CURRENCIES, money, pdfUrl } from "./common";

const RANGES = [["7", "7 days"], ["30", "30 days"], ["90", "90 days"], ["180", "6 months"], ["365", "1 year"]];

export default function RateAnalytics() {
  const ports = usePorts();
  const [f, setF] = useState({ origin_port_id: "", destination_port_id: "", container_type: "20FT", currency: "USD", provider: "", days: "90" });
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/sea-freight/rates/history", { params: f });
      setData(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const s = data?.stats;
  const cur = f.currency;

  return (
    <div data-testid="rate-analytics-page">
      <PageHead title="Rate History & Analytics"
        subtitle="Every recorded observation is kept — nothing is overwritten. Comparisons are normalised to one container type and one currency at a time."
        right={<>
          <Button data-testid="analytics-pdf" variant="outline" className="rounded-sm gap-1.5"
            onClick={() => window.open(pdfUrl("/sea-freight/export/rates.pdf", { origin_port_id: f.origin_port_id, destination_port_id: f.destination_port_id, container_type: f.container_type, currency: f.currency }), "_blank")}>
            <FileDown className="w-4 h-4" /> PDF
          </Button>
          <Button data-testid="analytics-excel" variant="outline" className="rounded-sm gap-1.5"
            onClick={() => window.open(pdfUrl("/sea-freight/export/rates.xlsx", { origin_port_id: f.origin_port_id, destination_port_id: f.destination_port_id, container_type: f.container_type, currency: f.currency }), "_blank")}>
            <Sheet className="w-4 h-4" /> Excel
          </Button>
        </>} />

      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <Field label="Origin port"><PortPicker testid="an-origin" ports={ports} value={f.origin_port_id} onChange={(v) => setF({ ...f, origin_port_id: v })} /></Field>
        <Field label="Destination port"><PortPicker testid="an-destination" ports={ports} value={f.destination_port_id} onChange={(v) => setF({ ...f, destination_port_id: v })} /></Field>
        <Field label="Container size">
          <Select value={f.container_type} onValueChange={(v) => setF({ ...f, container_type: v })}>
            <SelectTrigger data-testid="an-container" className="bg-white h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{CONTAINERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Currency">
          <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}>
            <SelectTrigger data-testid="an-currency" className="bg-white h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Period">
          <Select value={f.days} onValueChange={(v) => setF({ ...f, days: v })}>
            <SelectTrigger data-testid="an-days" className="bg-white h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{RANGES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
        <Stat testid="an-current" label="Current rate" value={s?.current != null ? money(s.current, cur) : "-"} hint="latest observation" />
        <Stat testid="an-previous" label="Previous rate" value={s?.previous != null ? money(s.previous, cur) : "-"} />
        <Stat testid="an-change" label="Change" value={s?.change_pct != null ? `${s.change_pct > 0 ? "+" : ""}${s.change_pct}%` : "-"}
          tone={s?.change_pct > 0 ? "red" : s?.change_pct < 0 ? "green" : "slate"} hint={s?.trend ? `trend ${s.trend}` : ""} />
        <Stat testid="an-high" label="Highest" value={s?.high != null ? money(s.high, cur) : "-"} />
        <Stat testid="an-low" label="Lowest" value={s?.low != null ? money(s.low, cur) : "-"} />
        <Stat testid="an-avg" label="Average" value={s?.average != null ? money(s.average, cur) : "-"} hint={`${s?.count || 0} points`} />
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="rate-chart">
        {data && data.series.length === 0 ? (
          <Empty testid="analytics-empty">No rate observations recorded for this lane, container size and currency yet.</Empty>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={data?.series || []} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip formatter={(v) => money(v, cur)} />
              <Line type="monotone" dataKey="total" stroke="#0B132B" strokeWidth={2} dot={{ r: 3 }} name="Total freight" />
            </LineChart>
          </ResponsiveContainer>
        )}
        {data?.comparison_note && <p className="text-[11px] text-amber-700 mt-2" data-testid="analytics-comparison-note">{data.comparison_note}</p>}
        {data?.note && <p className="text-[11px] text-slate-400 mt-2">{data.note}</p>}
      </div>
    </div>
  );
}
